import { useState } from "react";
import { ritualInput, type Ritual, type RitualInput, type RitualUpdate } from "@akhshy/types";
import type { UseResource } from "../../../lib/useResource.ts";
import { useForm } from "../../../lib/useForm.ts";
import { useSlugField } from "../../../lib/slug.ts";
import { FormField } from "../../molecules/FormField.tsx";
import { MetaFields } from "../../molecules/MetaFields.tsx";
import { MarkdownField } from "../../molecules/MarkdownField.tsx";
import { SlugField } from "../../molecules/SlugField.tsx";
import { TagInput } from "../../molecules/TagInput.tsx";
import { FormActions } from "../../molecules/FormActions.tsx";
import { Input } from "../../atoms/Input.tsx";

interface Props {
  initial: Ritual | null;
  onDone: () => void;
  resource: UseResource<Ritual, RitualInput, RitualUpdate>;
}

export function RitualForm({ initial, onDone, resource }: Props) {
  const { errors, submitting, submit } = useForm(ritualInput, "Ritual saved");
  const [week, setWeek] = useState(initial?.week ?? 1);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [displayDate, setDisplayDate] = useState(initial?.displayDate ?? "");
  const [content, setContent] = useState(initial?.content ?? "");
  const { slug, setSlug, suggestFrom } = useSlugField(initial?.slug ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [published, setPublished] = useState(initial?.published ?? true);
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit(
      { week, title, displayDate, content, slug: slug || undefined, tags, published, sortOrder },
      async (parsed) => {
      if (initial) await resource.update(initial.id, parsed);
      else await resource.create(parsed);
      onDone();
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="field-row">
        <FormField label="Week" error={errors["week"]}>
          <Input type="number" value={week} onChange={(e) => setWeek(Number(e.target.value))} />
        </FormField>
        <FormField label="Display date" error={errors["displayDate"]}>
          <Input value={displayDate} placeholder="Jul 3, 2026" onChange={(e) => setDisplayDate(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Title" error={errors["title"]}>
        <Input
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            suggestFrom(e.target.value);
          }}
        />
      </FormField>
      <SlugField slug={slug} onSlug={setSlug} error={errors["slug"]} />
      <MarkdownField label="Content (markdown)" value={content} onChange={setContent} rows={10} error={errors["content"]} />
      <TagInput value={tags} onChange={setTags} error={errors["tags"]} />
      <MetaFields
        published={published}
        sortOrder={sortOrder}
        onPublished={setPublished}
        onSortOrder={setSortOrder}
        error={errors["sortOrder"]}
      />
      <FormActions submitting={submitting} editing={initial !== null} onCancel={onDone} />
    </form>
  );
}
